import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtUsd, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../i18n";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Mic, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function FanDashboard() {
  const [payments, setPayments] = useState(null);
  const [busy, setBusy] = useState("");
  const { t, lang } = useLang();

  const load = useCallback(() => api.get("/payments/my").then(({ data }) => setPayments(data)).catch(() => setPayments([])), []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (sid) => {
    setBusy(sid);
    try { await api.post(`/payments/${sid}/confirm`); toast.success(t("fan_confirm_ok")); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const dispute = async (sid) => {
    const reason = window.prompt(t("fan_dispute_prompt")) ?? "";
    setBusy(sid);
    try { await api.post(`/payments/${sid}/dispute`, { reason }); toast.success(t("fan_dispute_ok")); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const escrowLabel = {
    held: { label: t("fan_held"), cls: "bg-amber-500/15 text-amber-400 border-amber-700/50" },
    pending_release: { label: t("fan_pending_release"), cls: "bg-sky-500/15 text-sky-400 border-sky-700/50" },
    disputed: { label: t("fan_disputed"), cls: "bg-red-500/15 text-red-400 border-red-800/50" },
    released: { label: t("fan_released"), cls: "bg-emerald-500/15 text-emerald-400 border-emerald-700/50" },
    pending: { label: t("fan_pending"), cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700" },
    refunded: { label: t("fan_refunded"), cls: "bg-red-500/15 text-red-400 border-red-800/50" },
  };

  return (
    <div data-testid="fan-dashboard" className="max-w-4xl mx-auto px-6 py-14">
      <h1 className="text-4xl font-black tracking-tighter text-white mb-2">{t("fan_title")}</h1>
      <p className="text-sm text-zinc-400 mb-10">{t("fan_sub")}</p>
      {payments === null ? (
        <p className="text-zinc-500">{t("loading")}</p>
      ) : payments.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-12 text-center">
          <p className="text-zinc-400 mb-4">{t("fan_empty")}</p>
          <Link to="/descubrir" data-testid="fan-discover-link" className="text-emerald-400 font-semibold hover:text-emerald-300">
            {t("fan_discover")}
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {payments.map((p) => {
            const e = escrowLabel[p.escrow_status] || escrowLabel.pending;
            const awaiting = p.payment_status === "paid" && p.escrow_status === "pending_release";
            return (
              <div key={p.session_id} data-testid="payment-row" className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-white">{p.tier_name} → {p.streamer_name}</p>
                    <p className="text-xs text-zinc-500 mt-1">{new Date(p.created_at).toLocaleString(lang)}</p>
                    {p.fan_message && <p className="text-xs text-emerald-300/80 italic mt-1">“{p.fan_message}”</p>}
                  </div>
                  <div className="flex items-center gap-4 flex-wrap justify-end">
                    <span className="text-lg font-bold text-white">{fmtUsd(p.amount_cents)}</span>
                    {p.queue_position && (
                      <Badge data-testid="fan-queue-position" variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-700/50 text-xs">
                        <Mic size={11} className="mr-1" /> {t("fan_mic_pos", { n: p.queue_position })}
                      </Badge>
                    )}
                    {p.payment_status === "paid" ? (
                      <Badge variant="outline" className={`${e.cls} text-xs`}>{e.label}</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
                        {{ pending: t("st_pending"), failed: t("st_failed"), expired: t("st_expired") }[p.payment_status] || p.payment_status}
                      </Badge>
                    )}
                  </div>
                </div>

                {awaiting && (
                  <div data-testid="fan-participation-panel" className="mt-4 pt-4 border-t border-zinc-800 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-zinc-400">{t("fan_confirm_prompt")}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {p.proof_url && (
                          <a href={p.proof_url} target="_blank" rel="noopener noreferrer" data-testid="fan-proof-link"
                            className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300">
                            <ExternalLink size={12} /> {t("fan_proof")}
                          </a>
                        )}
                        {p.dispute_deadline && (
                          <span className="text-[11px] text-zinc-500">{t("fan_deadline")} {new Date(p.dispute_deadline).toLocaleString(lang)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button data-testid="fan-confirm-btn" size="sm" disabled={busy === p.session_id}
                        className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                        onClick={() => confirm(p.session_id)}>
                        <CheckCircle2 size={14} className="mr-1.5" /> {t("fan_confirm_btn")}
                      </Button>
                      <Button data-testid="fan-dispute-btn" size="sm" variant="outline" disabled={busy === p.session_id}
                        className="rounded-full border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300 bg-transparent"
                        onClick={() => dispute(p.session_id)}>
                        <AlertTriangle size={14} className="mr-1.5" /> {t("fan_dispute_btn")}
                      </Button>
                    </div>
                    <p data-testid="fan-dispute-warning" className="w-full text-[11px] text-zinc-500 mt-1 sm:mt-0 sm:basis-full">{t("fan_dispute_warning")}</p>
                  </div>
                )}
                {p.escrow_status === "disputed" && (
                  <p data-testid="fan-disputed-note" className="mt-3 pt-3 border-t border-zinc-800 text-xs text-red-400/90">
                    {t("fan_disputed_note")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
